import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { apiFetch } from '@shared/api/http'

const base = '/admin/integrations/crm'
export const crmWebhookKey = ['admin-crm-webhook'] as const
const ConfigurationSchema = z.object({
  id: z.string(),
  url: z.string(),
  enabled: z.boolean(),
  createdAt: z.string().optional(),
})
const DeliverySchema = z.object({
  id: z.string(),
  configId: z.string(),
  status: z.enum(['PENDING', 'SENDING', 'DELIVERED', 'RETRYING', 'DEAD']),
  attempts: z.number(),
  httpStatus: z.number().nullable(),
  errorCode: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
const DeliveriesSchema = z.object({
  items: z.array(DeliverySchema),
  nextCursor: z.string().nullable(),
})
export type CrmDelivery = z.infer<typeof DeliverySchema>

export function useCrmConfiguration() {
  return useQuery({
    queryKey: [...crmWebhookKey, 'configuration'],
    queryFn: async () =>
      ConfigurationSchema.nullable().parse(await apiFetch<unknown>(base, { cache: 'no-store' })),
    retry: false,
  })
}
export function useCrmDeliveries(enabled: boolean) {
  return useInfiniteQuery({
    queryKey: [...crmWebhookKey, 'deliveries'],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '20' })
      if (pageParam) params.set('after', pageParam)
      return DeliveriesSchema.parse(
        await apiFetch<unknown>(`${base}/deliveries?${params}`, { cache: 'no-store' }),
      )
    },
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled,
    retry: false,
  })
}

// Keep the one-time secret outside React Query's query and mutation caches.
export async function configureCrmWebhook(url: string) {
  return ConfigurationSchema.extend({ secret: z.string() }).parse(
    await apiFetch<unknown>(base, { method: 'PUT', body: { url }, cache: 'no-store' }),
  )
}
export const disableCrmWebhook = () => apiFetch(base, { method: 'DELETE', cache: 'no-store' })
export const sendCrmWebhook = (userId: string, requestId: string) =>
  apiFetch(`${base}/users/${encodeURIComponent(userId)}/send`, {
    method: 'POST',
    body: { requestId },
    cache: 'no-store',
  })
export const redeliverCrmWebhook = (deliveryId: string) =>
  apiFetch(`${base}/deliveries/${encodeURIComponent(deliveryId)}/redeliver`, {
    method: 'POST',
    cache: 'no-store',
  })
